import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import axios from "axios";
import { API_URL } from "../config";

const OnboardingContext = createContext();

export const OnboardingProvider = ({ children }) => {
  const { user } = useAuth();
  const [onboardingProfile, setOnboardingProfile] = useState({
    email: "",
    name: "",
    google_id: "",
    account_type: "carrier",
    company_name: "",
    phone_number: "",
    mc_number: "",
    dot_number: "",
    dot_authorized: false,
    truck_type: "V",
    origin_city: "",
    truckstop_integration_id: "",
    ch_t_number: "",
  });
  const [currentStep, setCurrentStep] = useState(0);

  // Ensure we always run the carrier flow
  useEffect(() => {
    if (!onboardingProfile.account_type) {
      setOnboardingProfile((prev) => ({ ...prev, account_type: "carrier" }));
    }
  }, [onboardingProfile.account_type]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // useEffect(() => {
  //   localStorage.setItem(
  //     "onboarding_profile",
  //     JSON.stringify(onboardingProfile)
  //   );
  // }, [onboardingProfile]);
  // const savedProfile = localStorage.getItem("onboarding_profile");
  // console.log("data", savedProfile);

  // Load existing profile data on mount
  const loadProfileData = useCallback(async () => {
    try {
      setIsLoading(true);

      // ✅ Restore from localStorage first
      const savedProfile = localStorage.getItem("onboarding_profile");
      if (savedProfile) {
        setOnboardingProfile((prev) => ({
          ...prev,
          ...JSON.parse(savedProfile),
        }));
      }
      const jsonSaved = JSON.parse(savedProfile);
      console.log("data", jsonSaved);

      const token = localStorage.getItem("access_token");
      if (!token) return;

      const response = await axios.get(`${API_URL}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data) {
        setOnboardingProfile((prev) => ({
          ...prev,
          email: response.data.email || user?.email || "",
          name: response.data.name || user?.name || "",
          google_id: response.data.google_id || user?.google_id || "",
          // Default to carrier if missing
          account_type:
            response.data.account_type ||
            user?.account_type ||
            prev.account_type ||
            "carrier",
          company_name: response.data.company_name || "",
          phone_number: jsonSaved?.phone_number
            ? jsonSaved?.phone_number
            : "" || response.data.phone_number || "",
          mc_number: response.data.mc_number || "",
          dot_number: jsonSaved?.dot_number
            ? jsonSaved?.dot_number
            : "" || response.data.dot_number || "",
          dot_authorized: response.data.dot_authorized || false,
          truck_type: response.data.truck_type || "V",
          origin_city: response.data.origin_city || "",
          truckstop_integration_id: jsonSaved?.truckstop_integration_id
            ? jsonSaved?.truckstop_integration_id
            : "" || response.data.truckstop_integration_id || "",
          ch_t_number: response.data.ch_t_number || "",
        }));
      }
    } catch (err) {
      // Silently fail - allow onboarding to proceed without API call
      console.log(
        "Profile data not available (this is OK for testing):",
        err.message
      );
      // Initialize with user data if available
      if (user) {
        setOnboardingProfile((prev) => ({
          ...prev,
          email: user.email || "",
          name: user.name || "",
          account_type: user.account_type || "carrier",
        }));
      }
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    // Initialize with user data if available
    if (user) {
      setOnboardingProfile((prev) => ({
        ...prev,
        email: user.email || "",
        name: user.name || "",
        google_id: user.google_id || "",
        // Only use user's account_type if it exists, otherwise keep null for role selection
        account_type: user.account_type || "carrier",
      }));
    }
    // Load profile data
    loadProfileData();
  }, [user, loadProfileData]);

  // Force role selection if no account_type - reset to step 0
  useEffect(() => {
    if (!onboardingProfile.account_type && currentStep !== 0) {
      setCurrentStep(0);
    }
  }, [onboardingProfile.account_type, currentStep]);

  const updateOnboardingProfile = (updates) => {
    setOnboardingProfile((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const goToNextStep = () => {
    setCurrentStep((prev) => prev + 1);
    setError("");
  };

  const goToPrevStep = () => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
    setError("");
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    setError("");
  };

  const resetOnboarding = () => {
    setCurrentStep(0);
    setError("");
    loadProfileData();
  };

  return (
    <OnboardingContext.Provider
      value={{
        onboardingProfile,
        setOnboardingProfile: updateOnboardingProfile,
        currentStep,
        goToNextStep,
        goToPrevStep,
        goToStep,
        resetOnboarding,
        isLoading,
        error,
        setError,
        loadProfileData,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};
